import { useEffect, useRef } from 'react';
import { Brain } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import type { ChatMessage, SourceCitation } from '../../types';

interface MessageListProps {
  messages: ChatMessage[];
  onSourceClick?: (source: SourceCitation) => void;
  onSuggestion?: (message: string) => void;
}

const SUGGESTIONS = [
  'What are the inclusion criteria?',
  'What are the AE reporting requirements?',
  'How is study drug stored and dispensed?',
];

export function MessageList({ messages, onSourceClick, onSuggestion }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  // Auto-scroll to bottom when new messages arrive.
  // Use block: 'nearest' to scroll within the overflow container, not the
  // whole page. Jump instantly when a whole conversation loads at once;
  // animate only for live message turns.
  useEffect(() => {
    const added = messages.length - prevCountRef.current;
    prevCountRef.current = messages.length;
    if (messages.length === 0) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: added > 0 && added <= 2 ? 'smooth' : 'auto',
      block: 'nearest',
    });
  }, [messages]);

  // When the on-screen keyboard opens/closes the viewport height changes —
  // keep the conversation pinned to the bottom instead of leaving the last
  // message hidden behind the composer.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !window.matchMedia('(pointer: coarse)').matches) return;
    const handleResize = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-600">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
            Welcome to Site Intelligence
          </h3>
          <p className="mb-5 text-sm text-gray-600 dark:text-gray-400">
            Ask questions about your protocols, SOPs, and guidelines.
          </p>
          <div className="space-y-2 text-left">
            {SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => onSuggestion?.(suggestion)}
                disabled={!onSuggestion}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-primary-700 dark:hover:bg-primary-900/20"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4 md:space-y-5 md:px-6">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onSourceClick={onSourceClick} />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}
